/**
 * Brand-aligned confirmation dialog.
 *
 * Replaces the native `window.confirm()` popups (ugly, unstyled, browser
 * chrome leaks platform URL) with a modal that follows the Krea.AI /
 * wearedatalab.com visual guide:
 *   - card (ink-900) with hairline border
 *   - teal (primary) confirm CTA on the RIGHT
 *   - subtle cancel on the left
 *   - optional destructive tone → confirm CTA turns red
 *   - headline in the display font, body in sans
 *   - icon chip on the left reinforces the intent
 *
 * Usage (imperative — feels like window.confirm):
 *   const confirm = useConfirm();
 *   if (await confirm({ title, description, confirmLabel, tone })) { ... }
 *
 * The hook handles portal + state. Just wrap the app in <ConfirmProvider>.
 */
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    AlertTriangle,
    HelpCircle,
    LogIn,
    Trash2,
    X,
    RotateCcw,
} from "lucide-react";

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
    const [state, setState] = useState(null);
    const resolver = useRef(null);

    const confirm = useCallback((opts) => {
        return new Promise((resolve) => {
            resolver.current = resolve;
            setState({
                title: opts.title || "¿Estás seguro?",
                description: opts.description || "",
                confirmLabel: opts.confirmLabel || "Confirmar",
                cancelLabel: opts.cancelLabel || "Cancelar",
                // "neutral" | "destructive" | "primary"
                tone: opts.tone || "neutral",
                // "warn" | "question" | "delete" | "login" | "reset"
                kind: opts.kind || "question",
            });
        });
    }, []);

    const close = (value) => {
        if (resolver.current) resolver.current(value);
        resolver.current = null;
        setState(null);
    };

    const ctx = useMemo(() => ({ confirm }), [confirm]);

    return (
        <ConfirmContext.Provider value={ctx}>
            {children}
            {state && <Dialog {...state} onClose={close} />}
        </ConfirmContext.Provider>
    );
}

export function useConfirm() {
    const ctx = useContext(ConfirmContext);
    if (!ctx) {
        // Fallback for any component rendered outside the provider.
        // Keeps the call site identical so callers don't special-case.
        return async (opts) =>
            // eslint-disable-next-line no-alert
            window.confirm(`${opts?.title || ""}\n${opts?.description || ""}`);
    }
    return ctx.confirm;
}

const ICONS = {
    question: HelpCircle,
    warn: AlertTriangle,
    delete: Trash2,
    login: LogIn,
    reset: RotateCcw,
};

function Dialog({
    title,
    description,
    confirmLabel,
    cancelLabel,
    tone,
    kind,
    onClose,
}) {
    const btnRef = useRef(null);
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose(false);
            if (e.key === "Enter") onClose(true);
        };
        window.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        // Focus the primary CTA so Enter confirms.
        const t = setTimeout(() => btnRef.current?.focus(), 30);
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
            clearTimeout(t);
        };
    }, [onClose]);

    const Icon = ICONS[kind] || HelpCircle;
    const isDestructive = tone === "destructive";
    const iconChipClass = isDestructive
        ? "bg-destructive/15 text-destructive border-destructive/30"
        : kind === "warn"
          ? "bg-amber-400/15 text-amber-300 border-amber-400/30"
          : "bg-periwinkle-500/15 text-periwinkle-800 border-periwinkle-500/40";

    const confirmBtnClass = isDestructive
        ? "bg-destructive hover:bg-destructive/90 text-white"
        : "bg-periwinkle-500 hover:bg-periwinkle-400 text-periwinkle-ink";

    return (
        <div
            data-testid="confirm-dialog"
            className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in"
            onClick={(e) => e.target === e.currentTarget && onClose(false)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-dialog-title"
        >
            <div
                className="w-full max-w-md rounded-2xl border hairline border-ink-600 bg-ink-900 shadow-glow-lg overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <header className="flex items-start gap-4 p-6 border-b hairline border-ink-600">
                    <span
                        className={`shrink-0 w-11 h-11 rounded-xl border hairline flex items-center justify-center ${iconChipClass}`}
                        aria-hidden="true"
                    >
                        <Icon className="w-5 h-5" />
                    </span>
                    <div className="flex-1 min-w-0">
                        <h2
                            id="confirm-dialog-title"
                            data-testid="confirm-dialog-title"
                            className="font-display text-2xl text-fog-100 leading-tight"
                        >
                            {title}
                        </h2>
                        {description && (
                            <p
                                data-testid="confirm-dialog-description"
                                className="mt-2 text-sm text-fog-400 leading-relaxed"
                            >
                                {description}
                            </p>
                        )}
                    </div>
                    <button
                        data-testid="confirm-dialog-close"
                        onClick={() => onClose(false)}
                        className="w-8 h-8 rounded-full text-fog-500 hover:text-fog-100 flex items-center justify-center"
                        aria-label="Cerrar"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </header>
                <footer className="flex justify-end gap-3 px-6 py-4 bg-ink-900/50">
                    <button
                        data-testid="confirm-dialog-cancel"
                        onClick={() => onClose(false)}
                        className="rounded-xl border hairline border-ink-600 text-fog-300 hover:text-fog-100 hover:border-periwinkle-500/60 px-5 h-10 text-sm font-medium transition-colors"
                    >
                        {cancelLabel}
                    </button>
                    <button
                        ref={btnRef}
                        data-testid="confirm-dialog-confirm"
                        onClick={() => onClose(true)}
                        className={`inline-flex items-center gap-2 rounded-xl px-5 h-10 text-sm font-medium shadow-glow-sm transition-all ${confirmBtnClass}`}
                    >
                        {confirmLabel}
                    </button>
                </footer>
            </div>
        </div>
    );
}
